import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ScanBarcode,
  Package,
  Hash,
  Download,
  Printer,
  RefreshCcw,
  CheckCircle2,
  Search,
  AlertCircle,
  X,
} from "lucide-react";

import Modal from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";
import { ReactSelect } from "@/components/ui/react-select";
import { cn } from "@/lib/utils";
import apiClient from "@/api/apiClient";
import { API_ENDPOINTS } from "@/api/header";

const MODE_ALL_SERIALS = "product";
const MODE_SPECIFIC_SERIALS = "serials";

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export const ProductSerialBarcodeModal = ({
  open,
  onOpenChange,
  product = null,
  initialSerials = null,
  initialMode = null,
  onSuccess,
}) => {
  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (perm) => {
    if (!perm) return true;
    if (!permissions || permissions.length === 0) return false;
    if (Array.isArray(perm)) return perm.some((p) => permissions.includes(p));
    return permissions.includes(perm);
  };

  const [mode, setMode] = useState(MODE_ALL_SERIALS);
  const [manualSelectedProduct, setManualSelectedProduct] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [serialSearch, setSerialSearch] = useState("");
  const [selectedSerials, setSelectedSerials] = useState([]);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [isGenerated, setIsGenerated] = useState(false);

  const selectedProduct = product || manualSelectedProduct;

  const debouncedProductSearch = useDebounce(productSearch);
  const debouncedSerialSearch = useDebounce(serialSearch);

  // Revoke object URL on unmount or when URL changes
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        window.URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  const handleOpenChange = (val) => {
    if (!val) {
      if (pdfBlobUrl) {
        window.URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }
      setIsGenerated(false);
      setManualSelectedProduct(null);
      setSelectedSerials([]);
      setSerialSearch("");
      setProductSearch("");
      setMode(MODE_ALL_SERIALS);
    }
    onOpenChange(val);
  };

  // Query products when no pre-selected product is passed:
  // ONLY products with manage_inventory true AND tracking_type serial
  const { data: rawProducts = [], isLoading: isProductsLoading } = useQuery({
    queryKey: ["serial-barcode-eligible-products", debouncedProductSearch],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.PRODUCTS, {
        params: {
          per_page: 50,
          search: debouncedProductSearch || undefined,
        },
      });
      return res.data?.data || res.data || [];
    },
    enabled: open && !product && hasPermission("product read"),
  });

  const eligibleProducts = useMemo(() => {
    if (!Array.isArray(rawProducts)) return [];
    return rawProducts.filter((p) => {
      const hasInventory =
        Boolean(p.manage_inventory) ||
        p.manage_inventory === 1 ||
        p.manage_inventory === "1" ||
        p.manage_inventory === true;
      const isSerial =
        p.tracking_type === "serial" || Boolean(p.is_serialized);
      return hasInventory && isSerial;
    });
  }, [rawProducts]);

  const productOptions = useMemo(() => {
    return eligibleProducts.map((p) => ({
      value: String(p.id),
      label: p.name,
      sku: p.sku,
      product: p,
    }));
  }, [eligibleProducts]);

  // Query serial numbers for the selected product
  const { data: rawSerials = [], isLoading: isSerialsLoading } = useQuery({
    queryKey: [
      "serial-barcode-product-serials",
      selectedProduct?.id,
      debouncedSerialSearch,
    ],
    queryFn: async () => {
      if (!selectedProduct?.id) return [];
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.SERIALS, {
        params: {
          product_id: selectedProduct.id,
          search: debouncedSerialSearch || undefined,
          per_page: 100,
          with: "product",
        },
      });
      return res.data?.data || res.data || [];
    },
    enabled:
      open &&
      Boolean(selectedProduct?.id) &&
      (hasPermission("inventory serial track") ||
        hasPermission("inventory serial read") ||
        hasPermission("inventory read") ||
        hasPermission("product read")),
  });

  const serials = useMemo(() => {
    if (!Array.isArray(rawSerials)) return [];
    return rawSerials;
  }, [rawSerials]);

  // Synchronize initialSerials and initialMode when modal opens
  useEffect(() => {
    if (!open) return;

    if (initialMode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMode(initialMode);
    } else if (initialSerials && initialSerials.length > 0) {
      setMode(MODE_SPECIFIC_SERIALS);
    }

    if (initialSerials && initialSerials.length > 0) {
      const formatted = initialSerials.map((item) => {
        if (typeof item === "object" && item !== null) {
          const val = item.id || item.value || item.serial_number || item.serial;
          const lbl = item.serial_number || item.serial || item.label || String(val);
          return { value: val, label: lbl };
        }
        return { value: item, label: String(item) };
      });

      // Try to resolve string values to database IDs if rawSerials is loaded
      const resolved = formatted.map((item) => {
        if (rawSerials.length > 0) {
          const matched = rawSerials.find((rs) => {
            const rsNum = String(rs.serial_number || rs.serial || "").trim().toLowerCase();
            const itemLbl = String(item.label || item.value || "").trim().toLowerCase();
            return (
              String(rs.id) === String(item.value) ||
              (rsNum && rsNum === itemLbl)
            );
          });
          if (matched) {
            return {
              value: matched.id,
              label: matched.serial_number || matched.serial || item.label,
            };
          }
        }
        return item;
      });

      setSelectedSerials(resolved);
    }
  }, [open, initialSerials, initialMode, rawSerials]);

  // Mutation to generate the serial barcode report PDF
  const generateMutation = useMutation({
    mutationFn: async () => {
      if (!hasPermission("report serial barcode view")) {
        throw new Error("You do not have permission to generate barcode reports.");
      }
      if (!selectedProduct?.id) {
        throw new Error("Please select a product first.");
      }
      if (
        mode === MODE_SPECIFIC_SERIALS &&
        selectedSerials.length === 0
      ) {
        throw new Error("Please select at least one serial number.");
      }

      const params = {};
      if (mode === MODE_ALL_SERIALS) {
        params.product_id = selectedProduct.id;
      } else {
        const ids = [];
        const serialStrings = [];
        selectedSerials.forEach((s) => {
          if (typeof s.value === "number" || /^\d+$/.test(String(s.value))) {
            ids.push(s.value);
          } else {
            serialStrings.push(s.value);
          }
        });
        if (ids.length > 0) {
          params.serial_ids = ids;
        }
        if (serialStrings.length > 0) {
          params.serial_numbers = serialStrings;
        }
        if (!params.serial_ids && selectedSerials.length > 0) {
          params.serial_ids = selectedSerials.map((s) => s.value);
        }
      }

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.SERIAL_BARCODE,
        {
          params,
          responseType: "blob",
          paramsSerializer: (p) => {
            const parts = [];
            Object.entries(p).forEach(([key, val]) => {
              if (Array.isArray(val)) {
                val.forEach((v) =>
                  parts.push(`${key}[]=${encodeURIComponent(v)}`)
                );
              } else {
                parts.push(`${key}=${encodeURIComponent(val)}`);
              }
            });
            return parts.join("&");
          },
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      const blob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setIsGenerated(true);
      toast.success("Serial barcode labels generated successfully!");
      if (onSuccess) onSuccess();
    },
    onError: (error) => {
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Failed to generate serial barcode report. Please try again.";
      toast.error(msg);
    },
  });

  const handleDownload = () => {
    if (!pdfBlobUrl) return;
    const link = document.createElement("a");
    link.href = pdfBlobUrl;
    const cleanName = (selectedProduct?.name || "serial-barcodes").replace(
      /[^a-zA-Z0-9-_]/g,
      "_"
    );
    link.download = `serial-barcodes-${cleanName}-${Date.now()}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Barcode PDF downloaded!");
  };

  const handlePrint = () => {
    if (!pdfBlobUrl) return;

    // Use hidden iframe to trigger seamless print dialog
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = pdfBlobUrl;
    document.body.appendChild(iframe);

    iframe.onload = () => {
      if (iframe.contentWindow) {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.onafterprint = () => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
          };
          iframe.contentWindow.print();
        }, 200);
      } else {
        // Fallback to window.open
        window.open(pdfBlobUrl, "_blank")?.focus();
      }
    };
  };

  const handleSelectAllSerials = () => {
    const all = serials.map((s) => ({
      value: s.id,
      label: s.serial_number || s.serial,
    }));
    setSelectedSerials(all);
  };

  const handleClearSerials = () => {
    setSelectedSerials([]);
  };

  const toggleSerialSelection = (serial) => {
    const isSelected = selectedSerials.some(
      (s) =>
        String(s.value) === String(serial.id) ||
        String(s.label || "").trim().toLowerCase() ===
        String(serial.serial_number || serial.serial || "").trim().toLowerCase()
    );
    if (isSelected) {
      setSelectedSerials((prev) =>
        prev.filter(
          (s) =>
            String(s.value) !== String(serial.id) &&
            String(s.label || "").trim().toLowerCase() !==
            String(serial.serial_number || serial.serial || "").trim().toLowerCase()
        )
      );
    } else {
      setSelectedSerials((prev) => [
        ...prev,
        { value: serial.id, label: serial.serial_number || serial.serial },
      ]);
    }
  };

  const canGenerate =
    hasPermission("report serial barcode view") &&
    Boolean(selectedProduct?.id) &&
    (mode === MODE_ALL_SERIALS || selectedSerials.length > 0);

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      title="Serial Barcode Labels"
      icon={ScanBarcode}
      maxWidth="max-w-xl"
      footer={
        <div className="flex items-center justify-between w-full">
          {isGenerated ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsGenerated(false)}
                className="text-xs text-muted-foreground gap-1.5"
              >
                <RefreshCcw className="h-3.5 w-3.5" /> Back to Options
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleOpenChange(false)}
              >
                Done
              </Button>
            </>
          ) : (
            <div className="flex gap-2 justify-end w-full">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={!canGenerate || generateMutation.isPending}
                onClick={() => generateMutation.mutate()}
                className="gap-2 min-w-32"
                title={
                  !hasPermission("report serial barcode view")
                    ? "You lack permission to generate reports ('report serial barcode view')"
                    : ""
                }
              >
                {generateMutation.isPending ? (
                  <>
                    <Spinner size={16} className="mr-2" />
                    Generating…
                  </>
                ) : (
                  <>
                    <ScanBarcode className="h-4 w-4" />
                    Generate Labels
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      }
    >
      <div className="space-y-4 py-1">
        {!hasPermission("report serial barcode view") && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 text-xs text-destructive flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>
              You do not have permission to generate barcode reports (missing &ldquo;report serial barcode view&rdquo; permission).
            </span>
          </div>
        )}

        {/* State 1: Barcodes Ready (Success View) */}
        {isGenerated ? (
          <div className="flex flex-col items-center text-center py-4 px-2 space-y-4">
            <div className="h-14 w-14 bg-emerald-500/10 text-emerald-600 rounded-full flex items-center justify-center ring-4 ring-emerald-500/5">
              <CheckCircle2 className="h-7 w-7" />
            </div>

            <div className="space-y-1 max-w-md">
              <h4 className="text-base font-bold text-foreground">
                Barcode Labels Ready
              </h4>
              <p className="text-xs text-muted-foreground">
                Your serial barcode PDF has been prepared. Download the file or
                print it directly.
              </p>
            </div>

            {/* Generated Summary Card */}
            <Card className="w-full bg-muted/30 border">
              <CardContent className="p-4 text-left space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Product</span>
                  <span className="font-semibold text-foreground">
                    {selectedProduct?.name}
                  </span>
                </div>
                {selectedProduct?.sku && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">SKU</span>
                    <span className="font-mono text-muted-foreground">
                      {selectedProduct.sku}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between text-xs pt-1.5 border-t">
                  <span className="text-muted-foreground">Selection</span>
                  <Badge variant="secondary" className="font-medium text-[11px]">
                    {mode === MODE_ALL_SERIALS
                      ? "All Serial Numbers"
                      : `${selectedSerials.length} Specific Serial${selectedSerials.length === 1 ? "" : "s"
                      }`}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 gap-3 w-full pt-1">
              <Button
                type="button"
                variant="outline"
                onClick={handleDownload}
                className="gap-2 text-sm"
              >
                <Download className="h-4 w-4" /> Download PDF
              </Button>
              <Button
                type="button"
                onClick={handlePrint}
                className="gap-2 text-sm"
              >
                <Printer className="h-4 w-4" /> Print Labels
              </Button>
            </div>
          </div>
        ) : (
          /* State 2: Configuration & Selection View */
          <>
            {/* Product Header / Selector */}
            {product ? (
              <Card className="bg-muted/30 border">
                <CardContent className="p-3.5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 bg-background border rounded-lg text-primary shrink-0">
                      <Package className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 space-y-0.5">
                      <h4 className="font-semibold text-sm text-foreground leading-tight truncate">
                        {product.name}
                      </h4>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                        {product.sku && (
                          <span className="font-mono bg-background px-1.5 py-0.2 rounded border text-[11px]">
                            SKU: {product.sku}
                          </span>
                        )}
                        <Badge variant="outline" className="text-[10px] font-normal">
                          Serial Tracked
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-[11px] shrink-0 font-normal">
                    Managed Stock
                  </Badge>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Select Serial Product
                  </Label>
                  <span className="text-[11px] text-muted-foreground">
                    Only serial-tracked inventory products
                  </span>
                </div>
                <ReactSelect
                  options={productOptions}
                  isLoading={isProductsLoading}
                  value={
                    manualSelectedProduct
                      ? {
                        value: String(manualSelectedProduct.id),
                        label: manualSelectedProduct.name,
                        sku: manualSelectedProduct.sku,
                      }
                      : null
                  }
                  onChange={(opt) => {
                    setManualSelectedProduct(opt?.product || null);
                    setSelectedSerials([]);
                  }}
                  onInputChange={(val) => setProductSearch(val)}
                  placeholder="Search and choose a product…"
                  isClearable
                  noOptionsMessage={() =>
                    isProductsLoading
                      ? "Loading products…"
                      : "No serial-tracked inventory products found"
                  }
                  formatOptionLabel={(opt) => (
                    <div className="flex items-center justify-between py-0.5">
                      <span className="font-medium text-foreground text-sm">
                        {opt.label}
                      </span>
                      {opt.sku && (
                        <span className="font-mono text-xs text-muted-foreground">
                          {opt.sku}
                        </span>
                      )}
                    </div>
                  )}
                />
              </div>
            )}

            {/* Scope Selection Tabs */}
            <Tabs value={mode} onValueChange={setMode} className="w-full">
              <TabsList className="flex w-full h-auto bg-transparent border-b border-border p-0 gap-6 justify-start">
                <TabsTrigger
                  value={MODE_ALL_SERIALS}
                  className="flex items-center gap-2 pb-2.5 pt-1 px-1 text-sm font-medium rounded-none border-b-2 border-transparent bg-transparent shadow-none whitespace-nowrap data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:font-semibold data-[state=active]:bg-transparent data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-all cursor-pointer"
                >
                  <Package className="h-4 w-4" />
                  All Serials
                  {serials.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-muted text-muted-foreground font-normal">
                      {serials.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value={MODE_SPECIFIC_SERIALS}
                  className="flex items-center gap-2 pb-2.5 pt-1 px-1 text-sm font-medium rounded-none border-b-2 border-transparent bg-transparent shadow-none whitespace-nowrap data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:font-semibold data-[state=active]:bg-transparent data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-all cursor-pointer"
                >
                  <Hash className="h-4 w-4" />
                  Specific Serials
                  {selectedSerials.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-primary/10 text-primary font-semibold">
                      {selectedSerials.length}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Tab 1: All Serials Content */}
              <TabsContent value={MODE_ALL_SERIALS} className="pt-3 mt-0 space-y-3">
                <div className="rounded-lg border bg-muted/20 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <ScanBarcode className="h-4 w-4 text-primary" />
                    <span>Print All Recorded Serials</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    This option will generate barcode labels for all active serial numbers registered for this product.
                  </p>
                  {isSerialsLoading ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                      <Spinner size={14} /> Loading serial numbers…
                    </div>
                  ) : serials.length > 0 ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                      <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                      <span>
                        <strong>{serials.length}</strong> serial number{serials.length === 1 ? "" : "s"} ready to print.
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 pt-1">
                      <AlertCircle className="h-3.5 w-3.5" />
                      <span>No serial numbers currently recorded for this product.</span>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Tab 2: Specific Serials Content */}
              <TabsContent value={MODE_SPECIFIC_SERIALS} className="pt-3 mt-0 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">
                    Choose individual serial numbers to print:
                  </span>
                  {serials.length > 0 && (
                    <div className="flex items-center gap-2 text-xs">
                      <button
                        type="button"
                        onClick={handleSelectAllSerials}
                        className="text-primary hover:underline font-medium cursor-pointer"
                      >
                        Select All ({serials.length})
                      </button>
                      <span className="text-muted-foreground">•</span>
                      <button
                        type="button"
                        onClick={handleClearSerials}
                        className="text-muted-foreground hover:underline cursor-pointer"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>

                {/* Search Bar for Serials */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search serial numbers…"
                    value={serialSearch}
                    onChange={(e) => setSerialSearch(e.target.value)}
                    className="pl-9 h-9 text-xs"
                  />
                  {serialSearch && (
                    <button
                      type="button"
                      onClick={() => setSerialSearch("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                {/* Serials Scrollable List */}
                <div className="border rounded-lg max-h-52 overflow-y-auto divide-y bg-card">
                  {isSerialsLoading ? (
                    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                      <Spinner size={20} className="mb-2" />
                      <span className="text-xs">Loading serial numbers…</span>
                    </div>
                  ) : serials.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                      <AlertCircle className="h-6 w-6 text-muted-foreground/40 mb-1.5" />
                      <span className="text-xs font-medium text-foreground">
                        No serial numbers found
                      </span>
                      <span className="text-[11px] text-muted-foreground mt-0.5">
                        {serialSearch
                          ? `No matches for "${serialSearch}"`
                          : "This product currently has no recorded serial numbers."}
                      </span>
                    </div>
                  ) : (
                    serials.map((serial) => {
                      const isSelected = selectedSerials.some(
                        (s) =>
                          String(s.value) === String(serial.id) ||
                          String(s.label || "").trim().toLowerCase() ===
                          String(serial.serial_number || serial.serial || "").trim().toLowerCase()
                      );
                      const serialNum =
                        serial.serial_number || serial.serial || `ID: ${serial.id}`;
                      return (
                        <div
                          key={serial.id}
                          onClick={() => toggleSerialSelection(serial)}
                          className={cn(
                            "flex items-center justify-between p-2.5 text-xs cursor-pointer transition-colors",
                            isSelected ? "bg-muted/60" : "hover:bg-muted/30"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleSerialSelection(serial)}
                            />
                            <div className="flex items-center gap-2">
                              <ScanBarcode className="h-3.5 w-3.5 text-primary/60 shrink-0" />
                              <span className="font-mono font-medium text-foreground text-xs select-all">
                                {serialNum}
                              </span>
                            </div>
                          </div>
                          {serial.status && (
                            <Badge
                              variant={serial.status === "available" ? "success" : "outline"}
                              className="text-[10px] capitalize font-medium py-0 px-2"
                            >
                              {serial.status.replace(/_/g, " ")}
                            </Badge>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </Modal>
  );
};

ProductSerialBarcodeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  product: PropTypes.object,
  initialSerials: PropTypes.array,
  initialMode: PropTypes.string,
  onSuccess: PropTypes.func,
};

export default ProductSerialBarcodeModal;
